import { useEffect, useState } from 'react'
import { InitMsgFromSDK, onInitMessage, sendDonationReadyMessage } from '../utils/sdk-messenger'
import { IAAResponse, IAASuccessResponse } from 'models/IAA'
import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import configSlice from 'modules/config/state/config-slice'
import { IntelligentAskAmountsPredictionResponse } from 'modules/config/state/config-types'

const SAFEGUARD_TIMEOUT = 5000

// Used by DEVs and TEs to visit the app directly without the sdk
const SAFEGUARD_TOKEN_DEV_PARAM = 'safeguardtoken'

interface UseSDKSafeguardTokenCheckerProps {
  env?: string
}

const useSDKSafeguardTokenChecker = ({ env }: UseSDKSafeguardTokenCheckerProps) => {
  const [hasChecked, setHasChecked] = useState(false)

  // Assume to be embedded by sdk by default
  const [isEmbeddedBySDK, setIsEmbeddedBySDK] = useState(true)

  const dispatch = useDispatch<TypedDispatch<RootState>>()

  useEffect(() => {
    // Listen to the init message from the SDK
    onInitMessage((message: InitMsgFromSDK) => {
      if (!message.payload) {
        return
      }

      // Set Intelligent Ask Amounts
      let value: IntelligentAskAmountsPredictionResponse = {
        amountsHaveLoaded: true,
      }

      if (message.payload.iaa) {
        const intelligentAskAmountsResponse = message.payload.iaa as IAAResponse
        const isResponseSucessful = intelligentAskAmountsResponse.status === 200

        if (isResponseSucessful) {
          const successResponse = intelligentAskAmountsResponse as IAASuccessResponse

          value = {
            isDefaultRecurring: successResponse.data.isDefaultRecurring,
            showRecurringUpsell: successResponse.data.showRecurringUpsell,
            suggestedOnetimeAmount: successResponse.data.suggestedOnetimeAmount,
            suggestedOnetimeAmounts: successResponse.data.suggestedOnetimeAmounts,
            suggestedRecurringAmount: successResponse.data.suggestedRecurringAmount,
            suggestedRecurringAmounts: successResponse.data.suggestedRecurringAmounts,
            ...value,
          }
        }
      }

      dispatch(configSlice.actions.setIntelligentAskAmounts(value))

      setIsEmbeddedBySDK(
        message.payload.safeguardToken === process.env.NEXT_PUBLIC_SDK_SAFEGUARD_TOKEN,
      )

      setHasChecked(true)
    })

    // Signal to the SDK that the app is ready to receive messages
    sendDonationReadyMessage()

    if (hasChecked) return

    // After 2 seconds, if we haven't received a message from the SDK, set validation flag to false
    const timeoutId = setTimeout(() => {
      const hasSafeguardURLParam = !!new URLSearchParams(location.href).get(
        SAFEGUARD_TOKEN_DEV_PARAM,
      )

      if (hasSafeguardURLParam) return

      setIsEmbeddedBySDK(false)
    }, SAFEGUARD_TIMEOUT)

    return () => clearTimeout(timeoutId)
  }, [env, hasChecked])

  return isEmbeddedBySDK
}

export default useSDKSafeguardTokenChecker
